import { emit } from './emit'
import { ensurePageViewId } from './pageViewId'

/** Per-frame dwell tracker for landing pages.
 *  - Reading rule: visibleFraction ≥ 0.5 OR viewportCoverage ≥ 0.5
 *    (the OR-branch handles frames taller than the viewport),
 *    AND tab visible AND window focused.
 *  - Exit hysteresis: both ratios < 0.35 (prevents framer-motion flapping).
 *  - Flush: one event per pagehide / visibility-hidden / unload, idempotent.
 *  - Page-level: total_ms_on_page (wall-clock) + active_ms_on_page (visible AND focused). */

export type DwellExitReason = 'page_hide' | 'visibility_hidden' | 'unload'

interface FrameAccumulator {
    frameId: string
    frameIndex: number
    dwellMs: number
    visibleIntervals: number
    maxViewportCoverage: number
    reached: boolean
    scrolledPast: boolean
    isReading: boolean
    /** When the most recent accumulation interval started; null when not accumulating. */
    accumStartMs: number | null
}

interface DwellSession {
    pageId: string
    skuId: string | null
    startedAt: number
    frames: Map<string, FrameAccumulator>
    isPageVisible: boolean
    isPageFocused: boolean
    /** Time the tab was visible AND focused. Wall-clock is `now - startedAt`. */
    activeMs: number
    activeStartMs: number | null
    flushed: boolean
}

let session: DwellSession | null = null

export function beginDwellSession(args: { pageId: string; skuId?: string | null }): void {
    // Flush any prior leaked session (SPA nav within same SKU page is rare but possible).
    if (session && !session.flushed) flushDwellSession('unload')

    const startedAt = now()
    const isPageVisible = typeof document === 'undefined' ? true : document.visibilityState === 'visible'
    const isPageFocused = typeof document === 'undefined' ? true : document.hasFocus()
    const envInitiallyActive = isPageVisible && isPageFocused

    session = {
        pageId: args.pageId,
        skuId: args.skuId ?? null,
        startedAt,
        frames: new Map(),
        isPageVisible,
        isPageFocused,
        activeMs: 0,
        // Background-tab open: wait for visibility/focus before starting active clock.
        activeStartMs: envInitiallyActive ? startedAt : null,
        flushed: false,
    }
    ensurePageViewId()
}

export function endDwellSession(): void {
    session = null
}

/** Register a frame so it appears in the flush payload even if never reached. */
export function registerFrame(args: { frameId: string; frameIndex: number }): void {
    if (!session) return
    if (session.frames.has(args.frameId)) return
    session.frames.set(args.frameId, {
        frameId: args.frameId,
        frameIndex: args.frameIndex,
        dwellMs: 0,
        visibleIntervals: 0,
        maxViewportCoverage: 0,
        reached: false,
        scrolledPast: false,
        isReading: false,
        accumStartMs: null,
    })
}

export function unregisterFrame(frameId: string): void {
    if (!session) return
    const frame = session.frames.get(frameId)
    if (!frame) return
    // Settle any in-flight accumulation before the frame unmounts.
    settleAccumulation(frame, now())
}

interface VisibilitySample {
    visibleFraction: number // 0..1, share of the frame that's in the viewport
    viewportCoverage: number // 0..1, share of the viewport the frame fills
    scrolledPast: boolean // frame has scrolled above the viewport entirely
}

/** Pure visibility-rule evaluator (unit-testable without a DOM). */
export function evaluateReading(sample: VisibilitySample, previouslyReading: boolean): boolean {
    const enterRule = sample.visibleFraction >= 0.5 || sample.viewportCoverage >= 0.5
    if (!previouslyReading) return enterRule
    const exitRule = sample.visibleFraction < 0.35 && sample.viewportCoverage < 0.35
    return !exitRule
}

export function recordFrameVisibility(
    frameId: string,
    frameIndex: number,
    sample: VisibilitySample,
): void {
    if (!session) return
    let frame = session.frames.get(frameId)
    if (!frame) {
        registerFrame({ frameId, frameIndex })
        frame = session.frames.get(frameId)!
    }

    if (sample.viewportCoverage > frame.maxViewportCoverage) {
        frame.maxViewportCoverage = sample.viewportCoverage
    }
    if (sample.scrolledPast) frame.scrolledPast = true

    const nextReading = evaluateReading(sample, frame.isReading)
    reconcileFrameState(frame, nextReading, now())
}

export function setPageVisibility(isVisible: boolean): void {
    if (!session) return
    if (session.isPageVisible === isVisible) return
    session.isPageVisible = isVisible
    reconcileAllFramesEnvChange()
}

export function setPageFocus(isFocused: boolean): void {
    if (!session) return
    if (session.isPageFocused === isFocused) return
    session.isPageFocused = isFocused
    reconcileAllFramesEnvChange()
}

function envActive(s: DwellSession): boolean {
    return s.isPageVisible && s.isPageFocused
}

function reconcileFrameState(frame: FrameAccumulator, nextIntersectionReading: boolean, ts: number): void {
    if (!session) return
    const wasAccumulating = frame.isReading && frame.accumStartMs != null
    const isAccumulating = nextIntersectionReading && envActive(session)

    if (!wasAccumulating && isAccumulating) {
        frame.accumStartMs = ts
        frame.visibleIntervals += 1
        frame.reached = true
    } else if (wasAccumulating && !isAccumulating) {
        settleAccumulation(frame, ts)
    }
    frame.isReading = nextIntersectionReading
}

function reconcileAllFramesEnvChange(): void {
    if (!session) return
    const ts = now()
    const active = envActive(session)

    // Session-level active clock — independent of which frame is on screen.
    if (active && session.activeStartMs == null) {
        session.activeStartMs = ts
    } else if (!active && session.activeStartMs != null) {
        session.activeMs += Math.max(0, ts - session.activeStartMs)
        session.activeStartMs = null
    }

    session.frames.forEach((frame) => {
        const wasAccumulating = frame.accumStartMs != null
        const isAccumulating = frame.isReading && active

        if (!wasAccumulating && isAccumulating) {
            frame.accumStartMs = ts
            frame.visibleIntervals += 1
            frame.reached = true
        } else if (wasAccumulating && !isAccumulating) {
            settleAccumulation(frame, ts)
        }
    })
}

function settleAccumulation(frame: FrameAccumulator, ts: number): void {
    if (frame.accumStartMs == null) return
    frame.dwellMs += Math.max(0, ts - frame.accumStartMs)
    frame.accumStartMs = null
}

/** Idempotent — extra calls (e.g. pagehide + visibilitychange) are no-ops. */
export function flushDwellSession(exitReason: DwellExitReason): void {
    if (!session || session.flushed) return
    const ts = now()
    session.frames.forEach((frame) => settleAccumulation(frame, ts))
    if (session.activeStartMs != null) {
        session.activeMs += Math.max(0, ts - session.activeStartMs)
        session.activeStartMs = null
    }

    // Swallow the React 18 strict-mode cleanup-fired empty event:
    // unload + no frame reached + sub-500ms total = noise, not a real visit.
    const totalMs = Math.round(ts - session.startedAt)
    const anyFrameReached = Array.from(session.frames.values()).some((f) => f.reached)
    const looksLikeStrictModeNoise = exitReason === 'unload' && totalMs < 500 && !anyFrameReached
    session.flushed = true
    if (looksLikeStrictModeNoise) return

    const frames = Array.from(session.frames.values())
        .sort((a, b) => a.frameIndex - b.frameIndex)
        .map((f) => ({
            frame_id: f.frameId,
            frame_index: f.frameIndex,
            dwell_ms: Math.round(f.dwellMs),
            visible_intervals: f.visibleIntervals,
            max_viewport_coverage: Math.round(f.maxViewportCoverage * 1000) / 1000,
            reached: f.reached,
            scrolled_past: f.scrolledPast,
        }))

    // No `path`: warehouse joins page_id + sku_id to the SKUs table to
    // recover slug. Keeps the medical-service slug out of the payload
    // Segment auto-decorates with userId post-identify.
    emit('landing_page_frames_dwell', {
        page_view_id: ensurePageViewId(),
        page_id: session.pageId,
        sku_id: session.skuId,
        total_ms_on_page: Math.round(ts - session.startedAt),
        active_ms_on_page: Math.round(session.activeMs),
        exit_reason: exitReason,
        frames,
    })
}

/** Tests only. */
export function _peekDwellSessionForTests(): DwellSession | null {
    return session
}

function now(): number {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
}
